import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, ButtonBase, Typography, alpha } from "@mui/material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingRevisionOrigin,
  DocumentCraftingRevisionStatus,
  DocumentCraftingRevisionSummary,
  DocumentCraftingTranslationStatus,
} from "../../../Models/documentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { StatusChip } from "./DocumentCraftingUi";
import { formatDateTime, useUserName } from "./format";
import { useDocumentCraftingEnumLabels } from "./useDocumentCraftingEnumLabels";

const CHANGE_REQUEST_PREVIEW = 160;

const ORIGIN_TONES: Record<
  DocumentCraftingRevisionOrigin,
  "blue" | "gray" | "green"
> = {
  [DocumentCraftingRevisionOrigin.AI]: "blue",
  [DocumentCraftingRevisionOrigin.MANUAL_UPLOAD]: "gray",
  [DocumentCraftingRevisionOrigin.DUPLICATE]: "green",
};

interface Props {
  /** Saved revisions, newest first (as `DocumentDetail.revisions`). */
  revisions: DocumentCraftingRevisionSummary[];
  /** The caller's open draft, shown on top. */
  draft?: DocumentCraftingRevisionSummary | null;
  selectedRevisionId?: number | null;
  onSelect?: (revision: DocumentCraftingRevisionSummary) => void;
  emptyText?: string;
}

const Entry: React.FC<{
  revision: DocumentCraftingRevisionSummary;
  last: boolean;
  selected: boolean;
  onSelect?: (revision: DocumentCraftingRevisionSummary) => void;
}> = ({ revision, last, selected, onSelect }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const userName = useUserName();
  const { accent, accentSoft } = useDashboardColors();
  const [expanded, setExpanded] = useState(false);

  const isDraft = revision.status === DocumentCraftingRevisionStatus.DRAFT;
  const title = isDraft
    ? t("common.revisions.draft")
    : t("common.revisions.revision", { number: revision.number });
  const changeRequest = revision.change_request?.trim() ?? "";
  const longRequest = changeRequest.length > CHANGE_REQUEST_PREVIEW;
  const shownRequest =
    longRequest && !expanded
      ? `${changeRequest.slice(0, CHANGE_REQUEST_PREVIEW).trimEnd()}…`
      : changeRequest;
  const readyTranslations = revision.translations.filter(
    (item) => item.status === DocumentCraftingTranslationStatus.READY,
  );

  return (
    <Box
      component="li"
      sx={{ display: "flex", gap: 1.5, position: "relative" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: "13px",
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: isDraft ? color.white : accent,
            border: isDraft ? `2px dashed ${color.orange500}` : "none",
            boxShadow: isDraft ? "none" : `0 0 0 3px ${alpha(accent, 0.18)}`,
          }}
        />
        {!last && (
          <Box
            sx={{ width: 2, flex: 1, mt: 0.75, backgroundColor: color.gray200 }}
          />
        )}
      </Box>

      <Box
        onClick={onSelect ? () => onSelect(revision) : undefined}
        sx={{
          flex: 1,
          minWidth: 0,
          mb: last ? 0 : 1,
          px: 1.25,
          py: 1,
          borderRadius: "10px",
          cursor: onSelect ? "pointer" : "default",
          backgroundColor: selected ? accentSoft : "transparent",
          "&:hover": onSelect && !selected ? { backgroundColor: color.gray50 } : {},
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {onSelect ? (
            <ButtonBase
              onClick={(event) => {
                event.stopPropagation();
                onSelect(revision);
              }}
              aria-pressed={selected}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: color.gray900,
                borderRadius: "4px",
                "&:focus-visible": { outline: `2px solid ${color.gray700}` },
              }}
            >
              {title}
            </ButtonBase>
          ) : (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}>
              {title}
            </Typography>
          )}
          <StatusChip
            label={labels.origin(revision.origin)}
            tone={ORIGIN_TONES[revision.origin] ?? "gray"}
          />
          {isDraft && <StatusChip label={t("common.myDraft")} tone="orange" />}
        </Box>

        <Typography sx={{ fontSize: 12, color: color.gray500, mt: 0.25 }}>
          {isDraft || !revision.saved_at
            ? t("common.revisions.started", {
                user: userName(revision.created_by),
                date: formatDateTime(revision.created_at, i18n.language),
              })
            : t("common.revisions.saved", {
                user: userName(revision.saved_by),
                date: formatDateTime(revision.saved_at, i18n.language),
              })}
          {revision.model_name && <> · {revision.model_name}</>}
          {revision.base_revision_number !== null && (
            <>
              {" · "}
              {t("common.revisions.basedOn", {
                number: revision.base_revision_number,
              })}
            </>
          )}
        </Typography>

        {shownRequest && (
          <Typography
            sx={{
              fontSize: 13,
              color: color.gray700,
              mt: 0.5,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {shownRequest}
            {longRequest && (
              <Button
                size="small"
                color="secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded((value) => !value);
                }}
                sx={{ ml: 0.5, py: 0, minWidth: 0, fontSize: 12 }}
              >
                {expanded ? t("common.showLess") : t("common.showMore")}
              </Button>
            )}
          </Typography>
        )}
        {revision.refinements.length > 0 && (
          <Typography sx={{ fontSize: 12, color: color.gray500, mt: 0.25 }}>
            {t("common.revisions.refinements", {
              count: revision.refinements.length,
            })}
          </Typography>
        )}
        {revision.diff_summary && (
          <Typography
            sx={{ fontSize: 12, color: color.gray600, mt: 0.5, fontStyle: "italic" }}
          >
            {revision.diff_summary}
          </Typography>
        )}

        {(revision.findings_count > 0 || readyTranslations.length > 0) && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
            {revision.findings_count > 0 && (
              <StatusChip
                label={t("common.quality.count", {
                  count: revision.findings_count,
                })}
                tone="orange"
              />
            )}
            {readyTranslations.map((item) => (
              <StatusChip
                key={item.language}
                label={t("common.revisions.translation", {
                  language: labels.language(item.language),
                })}
                tone="gray"
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Revision history, newest first, as a line of dots (after DealTimeline);
 * rows select the revision when `onSelect` is given. */
const RevisionTimeline: React.FC<Props> = ({
  revisions,
  draft,
  selectedRevisionId,
  onSelect,
  emptyText,
}) => {
  const { t } = useTranslation("documentCrafting");
  const entries = draft ? [draft, ...revisions] : revisions;

  if (!entries.length) {
    return (
      <Typography sx={{ fontSize: 14, color: color.gray500 }}>
        {emptyText ?? t("common.revisions.empty")}
      </Typography>
    );
  }

  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {entries.map((revision, index) => (
        <Entry
          key={revision.id}
          revision={revision}
          last={index === entries.length - 1}
          selected={revision.id === selectedRevisionId}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
};

export default RevisionTimeline;
